import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import { useMaestroContext } from '../context/MaestroContext';
import { EtapaSetup } from '../models/ciclo';
import maestroService from '../maestroService';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { formatarDataHora } from '../utils/formatters';
import { ICONE_ETAPA_SETUP, LABEL_ETAPA_SETUP } from '../utils/labels';
import Card from '../components/Card';
import EstadoErro from '../components/EstadoErro';
import Header from '../components/Header';
import ModalCadastroSetupPendencia from '../components/ModalCadastroSetupPendencia';
import ModalCriarEscopo from '../components/ModalCriarEscopo';
import ModalGerenciarAreasProcessos from '../components/ModalGerenciarAreasProcessos';
import ModalGerenciarItensTemplate from '../components/ModalGerenciarItensTemplate';
import ModalGerenciarEscopoUsuarios from '../components/ModalGerenciarEscopoUsuarios';
import ModalGerenciarModelo from '../components/ModalGerenciarModelo';
import ModalGerenciarUsuariosMaestro from '../components/ModalGerenciarUsuariosMaestro';
import ModalResetarSetup from '../components/ModalResetarSetup';
import { SkeletonCard } from '../components/Skeletons';

// Etapas que já têm uma ação real conectada em algum ponto do módulo — pra essas, concluir a etapa
// exige que o dado real já exista, não aceita só o texto de evidência isolado. Só REVISAO fica de
// fora (é atestação por natureza — revisar o que já foi feito nas outras 7, não tem CRUD próprio).
const TIPOS_COM_ACAO_REAL = new Set(['MODELO', 'PERFIS_NIVEIS', 'RESPONSAVEIS', 'ESCOPOS', 'AREAS_PROCESSOS', 'TEMPLATES_SLA', 'EVIDENCIAS_REGRAS']);

// Perfis que representam responsabilidade operacional (etapa RESPONSAVEIS) — distinto de
// PERFIS_NIVEIS, que só exige QUALQUER usuário vinculado com QUALQUER perfil (nível de acesso).
const PERFIS_DE_RESPONSABILIDADE = new Set(['PREPARADOR', 'REVISOR', 'APROVADOR', 'RESPONSAVEL_AREA']);

const LABEL_BOTAO_ACAO_REAL: Record<string, string> = {
  MODELO: 'Criar modelo de fechamento',
  PERFIS_NIVEIS: 'Vincular usuários',
  RESPONSAVEIS: 'Definir preparador/revisor/aprovador',
  ESCOPOS: 'Cadastrar grupo econômico',
  AREAS_PROCESSOS: 'Cadastrar áreas e processos',
  TEMPLATES_SLA: 'Definir prazos (SLA)',
  EVIDENCIAS_REGRAS: 'Definir regras de evidência',
};

const MENSAGEM_PRECONDICAO: Record<string, string> = {
  MODELO: 'Nenhuma versão de modelo de fechamento criada ainda — crie uma antes de concluir esta etapa.',
  PERFIS_NIVEIS: 'Nenhum usuário vinculado ao Maestro ainda — vincule pelo menos um antes de concluir esta etapa.',
  RESPONSAVEIS: 'Nenhum preparador, revisor, aprovador ou responsável de área definido ainda — em "Usuários do Maestro", atribua um desses perfis a pelo menos um usuário antes de concluir esta etapa.',
  ESCOPOS: 'Nenhum grupo econômico/grupo de empresas cadastrado ainda — cadastre antes de concluir esta etapa.',
  AREAS_PROCESSOS: 'Nenhuma área ou processo cadastrado ainda — cadastre antes de concluir esta etapa.',
  TEMPLATES_SLA: 'Nenhuma atividade-modelo com prazo (SLA) definido ainda — defina antes de concluir esta etapa.',
  EVIDENCIAS_REGRAS: 'Nenhuma atividade-modelo com regra de evidência/SOX definida ainda — defina antes de concluir esta etapa.',
};

// Wizard sequencial (MAESTRO-RN-18) — mesmo padrão de navegação do onboarding do Workflow SOX
// (OnboardingWizardPage.tsx): etapa N só fica navegável se a etapa N-1 já estiver concluída. Sem
// isso, dá pra pular direto pra REVISAO sem ter configurado nada — a trava é só de navegação no
// cliente, o backend já bloqueia a habilitação real independente da ordem em que o usuário navega.
const MaestroSetupPage: React.FC = () => {
  const { usuario } = useAuth();
  const { getUsuario, gruposEconomicos, gruposEmpresas, usuarios, areas, empresasEscopo } = useMaestroContext();
  const [aplicandoPadrao, setAplicandoPadrao] = useState(false);
  const [etapaEmEdicao, setEtapaEmEdicao] = useState<EtapaSetup | null>(null);
  const [resetando, setResetando] = useState(false);
  const [gerenciandoUsuarios, setGerenciandoUsuarios] = useState(false);
  const [gerenciandoEscopo, setGerenciandoEscopo] = useState(false);
  const [criandoEscopo, setCriandoEscopo] = useState(false);
  const [gerenciandoModelo, setGerenciandoModelo] = useState(false);
  const [gerenciandoAreas, setGerenciandoAreas] = useState(false);
  const [gerenciandoItens, setGerenciandoItens] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [status, setStatus] = useState('AGUARDANDO_CONFIGURACAO');
  const [percentualGeral, setPercentualGeral] = useState(0);
  const [etapasExibidas, setEtapasExibidas] = useState<EtapaSetup[]>([]);
  const [indiceAtivo, setIndiceAtivo] = useState(0);

  const [contagens, setContagens] = useState({ templates: 0, processos: 0, itensComPrazo: 0, itensComRegra: 0 });

  const carregarSetup = () => {
    setCarregando(true);
    setErro(null);
    maestroService.obterSetup()
      .then(({ status: s, percentualConcluido, etapas }) => {
        setStatus(s);
        setPercentualGeral(percentualConcluido);
        setEtapasExibidas(etapas);
        setIndiceAtivo(prev => {
          const primeiroPendente = etapas.findIndex(e => !e.concluida);
          return prev < etapas.length ? prev : (primeiroPendente >= 0 ? primeiroPendente : 0);
        });
      })
      .catch((e: any) => setErro(e?.message ?? 'Não foi possível carregar o setup do Maestro.'))
      .finally(() => setCarregando(false));
  };

  const carregarContagens = () => {
    Promise.all([maestroService.listarTemplateVersions(), maestroService.listarProcessos()])
      .then(async ([templates, processos]) => {
        let itensComPrazo = 0;
        let itensComRegra = 0;
        for (const t of templates) {
          const itens = await maestroService.listarTemplateItems(t.id).catch(() => []);
          itensComPrazo += itens.filter((i: any) => i.prazoDiasUteis > 0).length;
          itensComRegra += itens.filter((i: any) => i.evidenciaObrigatoria || i.aprovacaoSoxObrigatoria).length;
        }
        setContagens({ templates: templates.length, processos: processos.length, itensComPrazo, itensComRegra });
      })
      .catch(() => {});
  };

  useEffect(carregarSetup, []);
  useEffect(carregarContagens, []);

  const podeIrPara = (indice: number): boolean => {
    for (let i = 0; i < indice; i++) {
      if (!etapasExibidas[i]?.concluida) return false;
    }
    return true;
  };

  const irPara = (indice: number) => { if (podeIrPara(indice)) setIndiceAtivo(indice); };

  const reabrirEtapa = (etapaId: number) =>
    maestroService.reabrirEtapaSetup(etapaId).then(carregarSetup).catch(() => dispararAcaoSimulada('Não foi possível reabrir a etapa.'));

  // Chamado a partir do checklist da etapa REVISAO — deixa editar qualquer etapa anterior sem
  // precisar voltar manualmente pelo stepper. Etapa concluída precisa reabrir primeiro (RN-18).
  const irEditarEtapaAnterior = (e: EtapaSetup) => {
    const indice = etapasExibidas.findIndex(x => x.id === e.id);
    if (indice < 0) return;
    if (e.concluida) reabrirEtapa(e.id).then(() => setIndiceAtivo(indice));
    else setIndiceAtivo(indice);
  };

  // Pré-condição de dado real por tipo de etapa — só se aplica às 7 que têm ação real conectada.
  const precondicaoAtendida = (tipo: string): boolean => {
    if (tipo === 'ESCOPOS') return gruposEconomicos.length > 0 && gruposEmpresas.length > 0;
    if (tipo === 'PERFIS_NIVEIS') return usuarios.length > 0;
    if (tipo === 'RESPONSAVEIS') return usuarios.some(u => PERFIS_DE_RESPONSABILIDADE.has(u.perfil));
    if (tipo === 'MODELO') return contagens.templates > 0;
    if (tipo === 'AREAS_PROCESSOS') return areas.length > 0 && contagens.processos > 0;
    if (tipo === 'TEMPLATES_SLA') return contagens.itensComPrazo > 0;
    if (tipo === 'EVIDENCIAS_REGRAS') return contagens.itensComRegra > 0;
    return true;
  };

  const evidenciaSugeridaReal = (tipo: string): string | undefined => {
    if (tipo === 'ESCOPOS') return `Grupo econômico "${gruposEconomicos[0]?.nome ?? '—'}" e ${gruposEmpresas.length} grupo(s) de empresas cadastrados.`;
    if (tipo === 'PERFIS_NIVEIS') return `${usuarios.length} usuário(s) vinculado(s) ao Maestro com perfil definido.`;
    if (tipo === 'RESPONSAVEIS') return `${usuarios.filter(u => PERFIS_DE_RESPONSABILIDADE.has(u.perfil)).length} usuário(s) com perfil de preparador/revisor/aprovador/responsável de área.`;
    if (tipo === 'MODELO') return `Modelo de fechamento criado (${contagens.templates} versão(ões)).`;
    if (tipo === 'AREAS_PROCESSOS') return `${areas.length} área(s) e ${contagens.processos} processo(s) cadastrados.`;
    if (tipo === 'TEMPLATES_SLA') return `${contagens.itensComPrazo} atividade(s)-modelo com prazo definido.`;
    if (tipo === 'EVIDENCIAS_REGRAS') return `${contagens.itensComRegra} atividade(s)-modelo com regra de evidência/SOX definida.`;
    if (tipo === 'REVISAO') {
      const etapasAnteriores = etapasExibidas.filter(e => e.tipo !== 'REVISAO');
      return `Revisão concluída — ${etapasAnteriores.filter(e => e.concluida).length}/${etapasAnteriores.length} etapas anteriores conferidas.`;
    }
    return undefined;
  };

  const abrirAcaoReal = (tipo: string) => {
    if (tipo === 'ESCOPOS') setCriandoEscopo(true);
    else if (tipo === 'PERFIS_NIVEIS' || tipo === 'RESPONSAVEIS') setGerenciandoUsuarios(true);
    else if (tipo === 'MODELO') setGerenciandoModelo(true);
    else if (tipo === 'AREAS_PROCESSOS') setGerenciandoAreas(true);
    else if (tipo === 'TEMPLATES_SLA' || tipo === 'EVIDENCIAS_REGRAS') setGerenciandoItens(true);
  };

  // RN-17 — pré-configuração padrão: cria de uma vez as áreas, processos e um modelo de fechamento
  // com atividades/dependências reais de um fechamento contábil mensal completo, já dentro do
  // escopo (grupo econômico/empresas) selecionado. Fica tudo em DRAFT, editável e removível pelas
  // mesmas telas usadas para cadastro manual — não substitui nada que já exista.
  const aplicarConfiguracaoPadrao = () => {
    if (empresasEscopo.length === 0) {
      dispararAcaoSimulada('Cadastre o grupo econômico e ao menos uma empresa (etapa "Escopos") antes de aplicar a configuração padrão.');
      return;
    }
    const confirmar = window.confirm(
      'Isso vai criar automaticamente ~21 áreas, seus processos e um modelo de fechamento com cerca de 35 atividades-modelo e suas dependências (Comercial → Faturamento → Fiscal → Contabilidade → Revisão → Workflow SOX), aplicados às empresas do escopo atual. Tudo fica editável e pode ser excluído item a item depois. Continuar?'
    );
    if (!confirmar) return;

    setAplicandoPadrao(true);
    maestroService.aplicarPadraoSetup(empresasEscopo.map(e => e.id))
      .then(resultado => {
        dispararAcaoSimulada(`Configuração padrão aplicada: ${resultado.areasCriadas} áreas, ${resultado.processosCriados} processos e ${resultado.itensCriados} atividades-modelo criadas. Revise e ajuste como preferir.`);
        carregarSetup();
        carregarContagens();
      })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível aplicar a configuração padrão.'))
      .finally(() => setAplicandoPadrao(false));
  };

  const fecharAcaoReal = (tipo: string) => {
    if (tipo === 'ESCOPOS') setCriandoEscopo(false);
    else if (tipo === 'PERFIS_NIVEIS' || tipo === 'RESPONSAVEIS') setGerenciandoUsuarios(false);
    else if (tipo === 'MODELO') setGerenciandoModelo(false);
    else if (tipo === 'AREAS_PROCESSOS') setGerenciandoAreas(false);
    else if (tipo === 'TEMPLATES_SLA' || tipo === 'EVIDENCIAS_REGRAS') setGerenciandoItens(false);
    carregarContagens();
  };

  const pendenciasBloqueantes = etapasExibidas.filter(e => !e.concluida);
  const setupCompleto = status === 'ATIVO' || (status !== 'BLOQUEADO_POR_CONFIGURACAO_DE_ACESSO' && pendenciasBloqueantes.length === 0);
  // MAESTRO-RN-20/RN-18 — mesmo critério do backend (GarantirAdminMaestro / EhAdminOuMaster): Master
  // da AI Númera OU o Administrador do Maestro Contábil designado pelo próprio cliente. Sem o
  // segundo caso, o cliente nunca conseguiria gerenciar o próprio acesso/reset sem depender do
  // Master da AI Númera para toda operação de rotina.
  const ehAdminMaestroOuMaster = usuario?.perfil === 'Master' || !!usuario?.adminMaestroContabil;

  if (carregando) {
    return (
      <div className='mc-page'>
        <Header title='Setup do Maestro' subtitle='Carregando...' />
        <SkeletonCard />
      </div>
    );
  }

  if (erro) {
    return (
      <div className='mc-page'>
        <Header title='Setup do Maestro' />
        <Card><EstadoErro descricao={erro} onTentarNovamente={carregarSetup} /></Card>
      </div>
    );
  }

  const etapa = etapasExibidas[indiceAtivo];

  return (
    <div className='mc-page'>
      <Header
        title='Setup do Maestro'
        subtitle='Configuração inicial conduzida pela área de Tecnologia da Informação — obrigatória antes da habilitação do módulo.'
        actions={(
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type='button'
              className='mc-btn mc-btn--secondary mc-btn--sm'
              disabled={aplicandoPadrao}
              title='Cria áreas, processos e um modelo de fechamento completo já pré-preenchidos, prontos para editar ou excluir'
              onClick={aplicarConfiguracaoPadrao}
            >
              <i className={`bi ${aplicandoPadrao ? 'bi-hourglass-split' : 'bi-magic'}`} /> {aplicandoPadrao ? 'Aplicando...' : 'Aplicar configuração padrão'}
            </button>
            <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setGerenciandoUsuarios(true)}>
              <i className='bi bi-people' /> Usuários do Maestro
            </button>
            {ehAdminMaestroOuMaster && (
              <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setGerenciandoEscopo(true)}>
                <i className='bi bi-shield-lock' /> Escopo de Acesso
              </button>
            )}
            {ehAdminMaestroOuMaster && (
              <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setResetando(true)}>
                <i className='bi bi-arrow-counterclockwise' /> Resetar setup
              </button>
            )}
          </div>
        )}
      />

      <Card>
        <div className='mc-setup-wizard__stepper'>
          {etapasExibidas.map((e, i) => {
            const disponivel = podeIrPara(i);
            const estado = e.concluida ? 'is-concluida' : i === indiceAtivo ? 'is-ativa' : disponivel ? '' : 'is-bloqueada';
            return (
              <React.Fragment key={e.id}>
                {i > 0 && <div className={`mc-setup-wizard__linha ${etapasExibidas[i - 1]?.concluida ? 'is-concluida' : ''}`} />}
                <button type='button' className={`mc-setup-wizard__passo ${estado}`} disabled={!disponivel} onClick={() => irPara(i)} title={disponivel ? e.titulo : 'Complete as etapas anteriores primeiro'}>
                  <span className='mc-setup-wizard__circulo'>{e.concluida ? <i className='bi bi-check-lg' /> : disponivel ? i + 1 : <i className='bi bi-lock-fill' />}</span>
                  <span>{e.titulo}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className='mc-progress mc-progress--grande'>
          <div className='mc-progress__track'><div style={{ width: `${percentualGeral}%` }} /></div>
          <span>{percentualGeral}%</span>
        </div>
        <p className='mc-texto-neutro' style={{ marginTop: 8, marginBottom: 0 }}>Status: {status}</p>
      </Card>

      {etapa && (
        <Card className='mc-setup-wizard__card'>
          <div className='mc-setup-wizard__card-topo'>
            <i className={`${ICONE_ETAPA_SETUP[etapa.tipo] ?? 'bi bi-gear'} mc-setup-wizard__icone`} />
            <div style={{ flex: 1 }}>
              <h2>Etapa {indiceAtivo + 1} de {etapasExibidas.length} — {etapa.titulo}</h2>
              <p>{etapa.descricao}</p>
            </div>
            <span className={`mc-badge mc-badge--${etapa.concluida ? 'success' : 'warning'}`}>{etapa.concluida ? 'Concluída' : 'Pendente'}</span>
          </div>

          {etapa.tipo === 'REVISAO' && (
            <ul className='mc-setup-item__pendencias mc-setup-item__pendencias--revisao' style={{ marginBottom: 14 }}>
              {etapasExibidas.filter(e => e.tipo !== 'REVISAO').map(e => (
                <li key={e.id} className={e.concluida ? '' : 'is-bloqueante'}>
                  <span><i className={`bi bi-${e.concluida ? 'check-circle-fill' : 'x-circle-fill'}`} /> {LABEL_ETAPA_SETUP[e.tipo] ?? e.titulo}</span>
                  <button type='button' className='mc-btn mc-btn--sm mc-btn--secondary' onClick={() => irEditarEtapaAnterior(e)}>Editar</button>
                </li>
              ))}
            </ul>
          )}

          {etapa.pendencias.length > 0 && (
            <ul className='mc-setup-item__pendencias'>
              {etapa.pendencias.map((p, i) => (
                <li key={i} className={p.bloqueante ? 'is-bloqueante' : ''}>
                  <i className={`bi bi-${p.bloqueante ? 'exclamation-octagon-fill' : 'info-circle'}`} /> {p.descricao}
                </li>
              ))}
            </ul>
          )}

          {etapa.concluida && etapa.evidenciaDescricao && (
            <div className='mc-setup-wizard__evidencia'>
              <strong>Evidência registrada</strong>
              {etapa.evidenciaDescricao} — {etapa.responsavelNome ?? getUsuario(etapa.responsavelId)?.nome ?? `usuário #${etapa.responsavelId}`}, {formatarDataHora(etapa.ultimaAtualizacao)}
            </div>
          )}

          {!etapa.concluida && TIPOS_COM_ACAO_REAL.has(etapa.tipo) && !precondicaoAtendida(etapa.tipo) && (
            <p className='mc-texto-danger'><i className='bi bi-exclamation-triangle' /> {MENSAGEM_PRECONDICAO[etapa.tipo]}</p>
          )}

          <div className='mc-setup-wizard__rodape'>
            <button type='button' className='mc-btn mc-btn--secondary' disabled={indiceAtivo === 0} onClick={() => setIndiceAtivo(i => i - 1)}>
              <i className='bi bi-arrow-left' /> Anterior
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              {etapa.concluida ? (
                <button type='button' className='mc-btn mc-btn--secondary' onClick={() => reabrirEtapa(etapa.id)}>Reabrir etapa</button>
              ) : (
                <>
                  {TIPOS_COM_ACAO_REAL.has(etapa.tipo) && (
                    <button type='button' className='mc-btn mc-btn--secondary' onClick={() => abrirAcaoReal(etapa.tipo)}>
                      <i className='bi bi-plus-lg' /> {LABEL_BOTAO_ACAO_REAL[etapa.tipo]}
                    </button>
                  )}
                  <button
                    type='button'
                    className='mc-btn mc-btn--primary'
                    disabled={TIPOS_COM_ACAO_REAL.has(etapa.tipo) && !precondicaoAtendida(etapa.tipo)}
                    title={TIPOS_COM_ACAO_REAL.has(etapa.tipo) && !precondicaoAtendida(etapa.tipo) ? MENSAGEM_PRECONDICAO[etapa.tipo] : undefined}
                    onClick={() => setEtapaEmEdicao(etapa)}
                  >
                    Configurar / Resolver pendência
                  </button>
                </>
              )}
              {indiceAtivo < etapasExibidas.length - 1 && (
                <button type='button' className='mc-btn mc-btn--secondary' disabled={!podeIrPara(indiceAtivo + 1)} onClick={() => setIndiceAtivo(i => i + 1)} title={!podeIrPara(indiceAtivo + 1) ? 'Conclua esta etapa antes de avançar' : undefined}>
                  Próxima <i className='bi bi-arrow-right' />
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className='mc-setup-habilitacao'>
          {!setupCompleto && (
            <p className='mc-texto-danger'><i className='bi bi-exclamation-triangle' /> Ainda há pendências obrigatórias — o Maestro não pode ser habilitado enquanto o setup estiver incompleto.</p>
          )}
          {status === 'AGUARDANDO_CONFIRMACAO' && (
            <p className='mc-texto-neutro'>Todas as etapas concluídas. Falta homologar — <strong>quem homologa não pode ser quem concluiu a etapa "Revisão final"</strong> (segregação de funções).</p>
          )}
          <button
            type='button'
            className='mc-btn mc-btn--primary'
            disabled={!setupCompleto || status === 'ATIVO'}
            title={!setupCompleto ? 'Resolva as pendências bloqueantes antes de habilitar o Maestro.' : undefined}
            onClick={() => maestroService.ativarSetup()
              .then(() => maestroService.homologarSetup('Habilitado via Cockpit'))
              .then(carregarSetup)
              .then(() => dispararAcaoSimulada('Maestro habilitado e registrado no log.'))
              .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível habilitar — verifique as pendências ou tente com outro usuário na homologação.'))}
          >
            {status === 'ATIVO' ? 'Maestro habilitado' : 'Habilitar o Maestro'}
          </button>
        </div>
      </Card>

      <ModalCadastroSetupPendencia
        etapa={etapaEmEdicao}
        onFechar={() => setEtapaEmEdicao(null)}
        onResolver={(etapaId, evidenciaDescricao) => maestroService.concluirEtapaSetup(etapaId, evidenciaDescricao).then(carregarSetup).catch(() => undefined)}
        evidenciaSugerida={etapaEmEdicao ? evidenciaSugeridaReal(etapaEmEdicao.tipo) : undefined}
      />
      <ModalResetarSetup aberto={resetando} onFechar={() => setResetando(false)} onResetado={() => { carregarSetup(); setIndiceAtivo(0); }} />
      <ModalGerenciarUsuariosMaestro aberto={gerenciandoUsuarios} onFechar={() => fecharAcaoReal('PERFIS_NIVEIS')} />
      <ModalGerenciarEscopoUsuarios aberto={gerenciandoEscopo} onFechar={() => setGerenciandoEscopo(false)} />
      <ModalCriarEscopo aberto={criandoEscopo} onFechar={() => fecharAcaoReal('ESCOPOS')} />
      <ModalGerenciarModelo aberto={gerenciandoModelo} onFechar={() => fecharAcaoReal('MODELO')} />
      <ModalGerenciarAreasProcessos aberto={gerenciandoAreas} onFechar={() => fecharAcaoReal('AREAS_PROCESSOS')} />
      <ModalGerenciarItensTemplate aberto={gerenciandoItens} onFechar={() => fecharAcaoReal('TEMPLATES_SLA')} />
    </div>
  );
};

export default MaestroSetupPage;
